import { z } from 'zod';
import { appIdSchema } from './app.js';
import { appPlanStateDTOSchema, planIntervalSchema } from './entitlement.js';
import { expectedPriceFields, minorUnitsSchema } from './expected-price.js';
/**
 * PER-LOCATION APP SUBSCRIPTIONS and the app billing page (handoff 0011).
 *
 * A location is explicitly SUBSCRIBED to an application from `/billing/apps/:appId`; creating or
 * deleting a location never changes the bill. The billed LOCATION quantity of an app is its
 * renewing (ACTIVE) subscriptions; an unsubscribed location keeps working until the end of the
 * paid period (ENDING, `endsAt`) and can be undone until then. `platform.max_locations` caps
 * subscribed (ACTIVE + ENDING) locations, never created ones.
 */
/** The applications whose usage is scoped to a location and which are therefore subscribed per
 *  location. Apps are code, not rows (docs/019 rule 5). */
export const LOCATION_SCOPED_APPS = ['visitor'];
export function isLocationScopedApp(app) {
    return LOCATION_SCOPED_APPS.includes(app);
}
/** In lock-step with the `LocationAppSubscriptionStatus` enum in schema.prisma, order included. */
export const LOCATION_APP_SUBSCRIPTION_STATUSES = ['ACTIVE', 'ENDING', 'ENDED'];
/**
 * What the billing page shows for one location — the stored status folded to what the owner
 * sees: `SUBSCRIBED` (ACTIVE), `ENDING` (unsubscribed, works until `endsAt`, undoable) or
 * `NOT_SUBSCRIBED` (no row, or ENDED — including an ENDING row whose `endsAt` has passed).
 */
export const LOCATION_SUBSCRIPTION_STATES = ['SUBSCRIBED', 'ENDING', 'NOT_SUBSCRIBED'];
export const locationSubscriptionStateSchema = z.enum(LOCATION_SUBSCRIPTION_STATES);
export const appBillingLocationDTOSchema = z
    .object({
    locationId: z.string().min(1),
    name: z.string().min(1),
    state: locationSubscriptionStateSchema,
    /** ISO-8601; set only when `state` is `ENDING`. */
    endsAt: z.string().datetime().nullable(),
})
    .strict();
/** What one more subscribed location costs per interval on the app's current plan — NULL when
 *  the plan does not bill per location (a flat or free plan). The live numbers come from the
 *  preview route; this is only for the "then $X/mo" label. */
export const locationUnitPriceDTOSchema = z
    .object({
    amount: minorUnitsSchema,
    currency: z.string().regex(/^[A-Z]{3}$/),
    interval: planIntervalSchema,
})
    .strict();
/**
 * The LOCATIONS section of an app's billing page (`#locations`) — every tenant location with its
 * subscription state. `cap` is the `platform.max_locations` entitlement (NULL = unlimited);
 * `subscribedCount` counts SUBSCRIBED + ENDING, which is what the cap limits.
 */
export const appBillingLocationSectionDTOSchema = z
    .object({
    dimension: z.literal('LOCATION'),
    cap: z.number().int().min(0).nullable(),
    subscribedCount: z.number().int().min(0),
    unitPrice: locationUnitPriceDTOSchema.nullable(),
    locations: z.array(appBillingLocationDTOSchema),
})
    .strict();
/** One section per billed dimension of the app. Only LOCATION exists today; a SEAT section
 *  joins the union when Directory is subscribed per seat. */
export const appBillingSectionDTOSchema = z.discriminatedUnion('dimension', [appBillingLocationSectionDTOSchema]);
/**
 * `GET /api/web/billing/apps/:appId` — the app billing page. `plan` is the header (plan, trial /
 * renewal status and date — the same `AppPlanStateDTO` `GET /billing/entitlements` carries for
 * this app), NULL when the tenant holds no plan for it.
 */
export const appBillingDTOSchema = z
    .object({
    app: appIdSchema,
    plan: appPlanStateDTOSchema.nullable(),
    sections: z.array(appBillingSectionDTOSchema),
})
    .strict();
/** Bound on each list of a staged change — far above any plan's location limit. */
export const MAX_STAGED_LOCATION_CHANGES = 1000;
const stagedLocationIdsSchema = z.array(z.string().min(1).max(64)).max(MAX_STAGED_LOCATION_CHANGES);
const changeSetFields = {
    /** Locations to subscribe now (charged now, prorated; free during a trial). */
    subscribe: stagedLocationIdsSchema.default([]),
    /** SUBSCRIBED locations to unsubscribe at the end of the paid period (at once in a trial). */
    unsubscribe: stagedLocationIdsSchema.default([]),
    /** ENDING locations to keep — back to SUBSCRIBED, nothing charged. */
    undo: stagedLocationIdsSchema.default([]),
};
function refineChangeSet(value, ctx) {
    const all = [...value.subscribe, ...value.unsubscribe, ...value.undo];
    if (all.length === 0) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'EMPTY_CHANGE_SET' });
        return;
    }
    if (new Set(all).size !== all.length) {
        ctx.addIssue({ code: z.ZodIssueCode.custom, message: 'LOCATION_IDS_DUPLICATE' });
    }
}
/**
 * `POST /api/web/billing/apps/:appId/changes/preview` — the staged set the page's "Your new bill"
 * summary prices. Every id appears at most once across the three lists; at least one is non-empty.
 */
export const appSubscriptionChangeSetSchema = z.object(changeSetFields).strict().superRefine(refineChangeSet);
/**
 * `POST /api/web/billing/apps/:appId/changes` — the staged set plus the amounts the owner was
 * shown for it (`expectedPriceFields`, docs/019 rule 8); 409 `PRICE_CHANGED` when stale.
 */
export const appSubscriptionChangeRequestSchema = z
    .object({ ...changeSetFields, ...expectedPriceFields })
    .strict()
    .superRefine(refineChangeSet);
/** `details` of 409 `LOCATION_SUBSCRIPTION_CONFLICT`: the staged action does not fit the
 *  location's current state (e.g. unsubscribing one that is not subscribed). */
export const locationSubscriptionConflictDetailsSchema = z.object({
    locationId: z.string(),
    action: z.enum(['subscribe', 'unsubscribe', 'undo']),
    /** NULL when the row moved between the check and the write (a concurrent change). */
    state: locationSubscriptionStateSchema.nullable(),
});
/** `details` of 409 `SUBSCRIBED_LOCATIONS_OVER_LIMIT`: a plan change or checkout whose location
 *  limit is below the number of subscribed locations. The UI links to the app billing page. */
export const subscribedLocationsOverLimitDetailsSchema = z.object({
    app: appIdSchema,
    limit: z.number().int().min(0),
    subscribedCount: z.number().int().min(0),
});
export function parseSubscribedLocationsOverLimitDetails(details) {
    const parsed = subscribedLocationsOverLimitDetailsSchema.safeParse(details);
    return parsed.success ? parsed.data : null;
}
/** `details` of 409 `LOCATION_SUBSCRIBED` on `DELETE /locations/:id`: the apps it is still
 *  subscribed to — the UI says "unsubscribe it on the Billing page first" and links there. */
export const locationSubscribedDetailsSchema = z.object({ apps: z.array(appIdSchema).min(1) });
export function parseLocationSubscribedDetails(details) {
    const parsed = locationSubscribedDetailsSchema.safeParse(details);
    return parsed.success ? parsed.data : null;
}
/**
 * `GET /api/web/locations/app-subscriptions` — one entry per LIVE subscription (SUBSCRIBED, or
 * ENDING with `endsAt` still ahead), for the admin Locations page's per-location app badge. Read
 * with `platform.location.read`, so a tenant admin without billing permission can see it. ENDED
 * rows (including an ENDING row past `endsAt`) are omitted.
 */
export const locationAppSubscriptionSummaryDTOSchema = z
    .object({
    locationId: z.string().min(1),
    app: appIdSchema,
    state: z.enum(['SUBSCRIBED', 'ENDING']),
    /** ISO-8601; set only when `state` is `ENDING`. */
    endsAt: z.string().datetime().nullable(),
})
    .strict();
//# sourceMappingURL=location-app-subscription.js.map